import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import type { ColumnDef } from "@tanstack/react-table";
import {
  adminApi,
  type ListPoolTagsInput,
  type PoolTagRow,
  type TagOrigin,
  type TagPoolStatus,
  type TagType,
  tagAliasNoun,
} from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/DataTable";
import { DetailDrawer, DetailRow } from "@/components/DetailDrawer";
import { StatusPill } from "@/components/StatusPill";
import { queryTableProps } from "@/lib/data-table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { poolTagTone } from "@/lib/status-tone";
import { reasonLabel, REVOKED_REASON_OPTIONS, tagListFilters } from "@/lib/referrals";

export const Route = createFileRoute("/_authed/tags")({
  component: TagsPage,
});

const STATUSES: ReadonlyArray<"" | TagPoolStatus> = [
  "",
  "inventory",
  "unassigned",
  "assigned",
  "paused",
  "revoked",
];
const TYPES: ReadonlyArray<"" | TagType> = ["", "qr_sticker", "qr_label", "nfc", "digital"];
const ORIGINS: ReadonlyArray<"" | TagOrigin> = ["", "physical", "generated"];
const PAGE_SIZE = 50;

function TagsPage() {
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("");
  const [revokedReason, setRevokedReason] = useState("");
  const [type, setType] = useState<(typeof TYPES)[number]>("");
  const [origin, setOrigin] = useState<(typeof ORIGINS)[number]>("");
  const [batchId, setBatchId] = useState("");
  const [userId, setUserId] = useState("");
  // Typed filters key a server query: settle them before fetching.
  const debouncedBatchId = useDebouncedValue(batchId);
  const debouncedUserId = useDebouncedValue(userId);
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);

  const filters: ListPoolTagsInput = useMemo(
    () => ({
      ...tagListFilters(status, revokedReason),
      type: type || undefined,
      origin: origin || undefined,
      batchId: debouncedBatchId.trim() || undefined,
      userId: debouncedUserId.trim() || undefined,
      limit: PAGE_SIZE,
    }),
    [status, revokedReason, type, origin, debouncedBatchId, debouncedUserId],
  );

  const query = useInfiniteQuery({
    queryKey: queryKeys.tags.list(filters),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.listPoolTags()({ ...filters, cursor: pageParam ?? null });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = query.data?.pages.flatMap((p) => p.tags) ?? [];

  const detail = useQuery({
    enabled: !!selectedTagId,
    queryKey: queryKeys.tags.detail(selectedTagId ?? ""),
    queryFn: async () => {
      const res = await adminApi.getPoolTag()({ tagId: selectedTagId! });
      return res.data;
    },
  });

  const columns = useMemo<ColumnDef<PoolTagRow, unknown>[]>(
    () => [
      {
        header: "Tag ID",
        accessorKey: "tagId",
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.tagId}</span>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <span className="inline-flex flex-wrap items-center gap-1">
            <StatusPill tone={poolTagTone(row.original.status)}>{row.original.status}</StatusPill>
            {row.original.status === "paused" && row.original.pausedReason ? (
              <StatusPill tone="warning">{reasonLabel(row.original.pausedReason)}</StatusPill>
            ) : null}
            {row.original.revokedReason ? (
              <StatusPill tone="danger">{reasonLabel(row.original.revokedReason)}</StatusPill>
            ) : null}
          </span>
        ),
      },
      { header: "Type", accessorKey: "type" },
      {
        header: "Alias #",
        accessorKey: "aliasSeq",
        cell: ({ row }) =>
          row.original.aliasSeq != null ? (
            <span className="text-xs">{tagAliasNoun(row.original.type)} #{row.original.aliasSeq}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        header: "Origin",
        accessorKey: "origin",
        cell: ({ row }) => (
          <span className="text-xs">
            {row.original.origin === "generated"
              ? `generated · ${row.original.entitlement ?? "—"}`
              : "physical"}
          </span>
        ),
      },
      {
        header: "Batch",
        accessorKey: "batchId",
        cell: ({ row }) => (
          <span className="font-mono text-[10px] text-muted-foreground">
            {row.original.batchId || "—"}
          </span>
        ),
      },
      {
        header: "Owner",
        accessorKey: "userId",
        cell: ({ row }) =>
          row.original.userId ? (
            <span className="font-mono text-[10px]">{row.original.userId.slice(0, 8)}…</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        header: "Claimed",
        accessorKey: "claimedAtMillis",
        cell: ({ row }) =>
          row.original.claimedAtMillis ? (
            <span className="text-xs">
              {format(new Date(row.original.claimedAtMillis), "yyyy-MM-dd")}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        header: "Item",
        accessorKey: "itemId",
        cell: ({ row }) =>
          row.original.itemId ? (
            <span className="font-mono text-[10px]">{row.original.itemId.slice(0, 8)}…</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Tags</h1>
        <p className="text-sm text-muted-foreground">
          All pool tags — physical (inventory → unassigned → assigned) and generated
          (signup / subscriber slots; paused while a subscription has lapsed).
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div>
            <label className="text-xs text-muted-foreground">Status</label>
            <select
              className="block rounded border bg-background px-2 py-1 text-sm"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as (typeof STATUSES)[number]);
                setRevokedReason("");
              }}
            >
              {STATUSES.map((s) => (
                <option key={s || "any"} value={s}>
                  {s || "any"}
                </option>
              ))}
            </select>
          </div>
          {status === "revoked" ? (
            <div>
              <label className="text-xs text-muted-foreground">Revoked reason</label>
              <select
                className="block rounded border bg-background px-2 py-1 text-sm"
                value={revokedReason}
                onChange={(e) => setRevokedReason(e.target.value)}
              >
                <option value="">any</option>
                {REVOKED_REASON_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {reasonLabel(r)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div>
            <label className="text-xs text-muted-foreground">Type</label>
            <select
              className="block rounded border bg-background px-2 py-1 text-sm"
              value={type}
              onChange={(e) => setType(e.target.value as (typeof TYPES)[number])}
            >
              {TYPES.map((t) => (
                <option key={t || "any"} value={t}>
                  {t || "any"}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Origin</label>
            <select
              className="block rounded border bg-background px-2 py-1 text-sm"
              value={origin}
              onChange={(e) => setOrigin(e.target.value as (typeof ORIGINS)[number])}
            >
              {ORIGINS.map((o) => (
                <option key={o || "any"} value={o}>
                  {o || "any"}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Batch ID</label>
            <Input
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              placeholder="batch_…"
              className="w-56"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Owner UID</label>
            <Input
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="uid"
              className="w-56"
            />
          </div>
        </CardContent>
      </Card>

      <DataTable
        data={rows}
        columns={columns}
        isFetchingMore={query.isFetchingNextPage}
        hasMore={!!query.hasNextPage}
        onLoadMore={() => query.fetchNextPage()}
        onRowClick={(row) => setSelectedTagId(row.tagId)}
        rowKey={(row) => row.tagId}
        emptyMessage="No tags match these filters."
        {...queryTableProps(query)}
      />

      {selectedTagId ? (
        <DetailDrawer
          title={detail.data ? `Tag ${detail.data.tagId}` : "Loading…"}
          subtitle={detail.data?.batchId}
          onClose={() => setSelectedTagId(null)}
        >
          {detail.isLoading || !detail.data ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : (
            <div className="space-y-2">
              <DetailRow label="Status">
                <StatusPill tone={poolTagTone(detail.data.status)}>{detail.data.status}</StatusPill>
              </DetailRow>
              <DetailRow label="Type">{detail.data.type}</DetailRow>
              <DetailRow label="Alias">
                {detail.data.alias ?? <span className="text-muted-foreground">— (not yet owned)</span>}
              </DetailRow>
              <DetailRow label="Origin">
                {detail.data.origin === "generated"
                  ? `generated · ${detail.data.entitlement ?? "—"}`
                  : "physical"}
              </DetailRow>
              <DetailRow label="Batch">
                <span className="font-mono text-xs">{detail.data.batchId || "—"}</span>
              </DetailRow>
              <DetailRow label="Generated">
                {format(new Date(detail.data.generatedAtMillis), "yyyy-MM-dd HH:mm")}
              </DetailRow>
              <DetailRow label="Generated by">
                <span className="font-mono text-xs">{detail.data.generatedBy}</span>
              </DetailRow>
              <DetailRow label="Owner">
                {detail.data.userId ? (
                  <span className="font-mono text-xs">
                    {detail.data.userId}
                    {detail.data.ownerEmail ? ` — ${detail.data.ownerEmail}` : ""}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </DetailRow>
              <DetailRow label="Claimed">
                {detail.data.claimedAtMillis
                  ? format(new Date(detail.data.claimedAtMillis), "yyyy-MM-dd HH:mm")
                  : "—"}
              </DetailRow>
              <DetailRow label="Released">
                {detail.data.releasedAtMillis
                  ? format(new Date(detail.data.releasedAtMillis), "yyyy-MM-dd HH:mm")
                  : "—"}
              </DetailRow>
              <DetailRow label="Item">
                {detail.data.itemId ? (
                  <span className="font-mono text-xs">
                    {detail.data.itemId}
                    {detail.data.itemName ? ` — ${detail.data.itemName}` : ""}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </DetailRow>
              <DetailRow label="Assigned">
                {detail.data.assignedAtMillis
                  ? format(new Date(detail.data.assignedAtMillis), "yyyy-MM-dd HH:mm")
                  : "—"}
              </DetailRow>
              {detail.data.status === "paused" && detail.data.pausedReason ? (
                <DetailRow label="Paused">{reasonLabel(detail.data.pausedReason)}</DetailRow>
              ) : null}
              {detail.data.revokedAtMillis ? (
                <DetailRow label="Revoked">
                  {format(new Date(detail.data.revokedAtMillis), "yyyy-MM-dd HH:mm")}
                  {detail.data.revokedReason ? ` — ${detail.data.revokedReason}` : ""}
                </DetailRow>
              ) : null}
            </div>
          )}
        </DetailDrawer>
      ) : null}
    </div>
  );
}
